import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, IconButton, Stack, TableSortLabel, MenuItem, Select, FormControl, InputLabel, Chip, CircularProgress, Pagination } from '@mui/material';
import { useLocation } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HistoryIcon from '@mui/icons-material/History';
import api from '../api/axios';
import UserImportDialog from '../components/UserImportDialog';

interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  referral_id?: string;
  affiliate_id?: string;
  is_active: boolean;
  credit_balance: string;
  bonus_balance: string;
  vip_balance: string;
  loyalty_balance: string;
  individual_ggr: number;
  registration_ip?: string;
  last_login_ip?: string;
  last_login?: string;

  // Fraud detection fields
  fraud_score?: number | null;
  device_id?: string | null;
  last_fraud_check?: string | null;
  fraud_check_count?: number;
}



interface PlatformCredential {
  id: number;
  platform_id: number;
  platform_name: string;
  platform_user_id: string;
  platform_username: string;
  platform_password: string;
  is_active: boolean;
  extra_data: any;
}

interface Transaction {
  id: number;
  transaction_type: string;
  amount: number;
  net_amount: number;
  payment_method: string;
  initiated_at: string;
  completed_at: string | null;
  description: string;
  payment_reference: string | null;
  payment_metadata: any;
}

const emptyUser: Partial<User> = {
  email: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  referral_id: '',
  affiliate_id: '',
  is_active: true,
};

interface TenantUsersPageProps {
  tenantId?: number;
}

const TenantUsersPage: React.FC<TenantUsersPageProps> = ({ tenantId }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [form, setForm] = useState<any>({ ...emptyUser, password: '' });
  const [balanceDialog, setBalanceDialog] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [balanceForm, setBalanceForm] = useState({
    credit: '',
    credit_op: 'add',
    bonus: '',
    bonus_op: 'add',
    vip: '',
    vip_op: 'add',
    loyalty: '',
    loyalty_op: 'add',
  });
  const [platformCredentialsDialog, setPlatformCredentialsDialog] = useState<{ open: boolean; user: User | null; credentials: PlatformCredential[] }>({ open: false, user: null, credentials: [] });
  const [editingCredential, setEditingCredential] = useState<{ credential: PlatformCredential | null; username: string; password: string }>({ credential: null, username: '', password: '' });
  const [historyDialog, setHistoryDialog] = useState<{ open: boolean; user: User | null; transactions: Transaction[]; loading: boolean }>({ open: false, user: null, transactions: [], loading: false });
  const [importOpen, setImportOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'updated_at' | 'created_at' | 'affiliate_id' | 'credit_balance' | 'individual_ggr' | 'email' | 'first_name' | 'last_name'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({
    total_users: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isManualSearch, setIsManualSearch] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isInitialized) {
      fetchUsers();
    }
    // eslint-disable-next-line
  }, [tenantId, search, sortBy, sortOrder, page, pageSize, isInitialized]);

  // Handle URL search parameters with delay to prevent race conditions
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    
    if (searchParam && searchParam !== search && !isManualSearch) {
      // Add a small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        setSearch(searchParam);
        setPage(1); // Reset to first page when searching
        setIsInitialized(true);
      }, 100);
      
      return () => clearTimeout(timer);
    } else if (!isInitialized) {
      // Mark as initialized if no search param
      setIsInitialized(true);
    }
  }, [location.search, search, isInitialized, isManualSearch]);

  // Reset manual search flag when search is cleared or URL changes
  useEffect(() => {
    if (search === '' || location.search === '') {
      setIsManualSearch(false);
    }
  }, [search, location.search]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = '/api/v1/admin/user-management/users/?';
      if (tenantId) url += `tenant_id=${tenantId}&`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (sortBy) url += `sort_by=${sortBy}&`;
      if (sortOrder) url += `sort_order=${sortOrder}&`;
      url += `page=${page}&page_size=${pageSize}`;
      const res = await api.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data as { users: User[]; pagination: any };
      setUsers(data.users);
      setPagination(data.pagination);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch users', severity: 'error' });
    }
  };

  const handleSort = (column: 'updated_at' | 'created_at' | 'affiliate_id' | 'credit_balance' | 'individual_ggr' | 'email' | 'first_name' | 'last_name') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const createSortHandler = (column: 'updated_at' | 'created_at' | 'affiliate_id' | 'credit_balance' | 'individual_ggr' | 'email' | 'first_name' | 'last_name') => () => {
    handleSort(column);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when searching
    setIsManualSearch(true); // Mark that user is manually editing search
  };

  const handleOpenDialog = (user?: User) => {
    setEditUser(user || null);
    setForm(user ? { ...user, password: '' } : { ...emptyUser, password: '' });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditUser(null);
    setForm({ ...emptyUser, password: '' });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editUser) {
        // Edit existing user
        const updateData = { ...form };
        if (!updateData.password) delete updateData.password;
        await api.put(`/api/v1/users/${editUser.id}`, updateData, { headers: { Authorization: `Bearer ${token}` } });
        setSnackbar({ open: true, message: 'User updated', severity: 'success' });
      } else {
        // Create new user
        // Backend needs tenant_id and a role; blank optional fields are sent as null rather than ''
        const createData: any = { ...form, role_name: 'player' };
        if (tenantId) createData.tenant_id = tenantId;
        ['phone_number', 'referral_id', 'affiliate_id'].forEach(k => {
          if (typeof createData[k] === 'string' && !createData[k].trim()) delete createData[k];
        });
        await api.post('/api/v1/users/', createData, { headers: { Authorization: `Bearer ${token}` } });
        setSnackbar({ open: true, message: 'User created', severity: 'success' });
      }
      fetchUsers();
      handleCloseDialog();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : Array.isArray(detail) && detail[0]?.msg ? detail[0].msg : 'Failed to save user';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/v1/users/${user.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSnackbar({ open: true, message: 'User deleted', severity: 'success' });
      fetchUsers();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    }
  };

  const handleOpenBalanceDialog = (user: User) => {
    setBalanceDialog({ open: true, user });
    setBalanceForm({
      credit: '',
      credit_op: 'add',
      bonus: '',
      bonus_op: 'add',
      vip: '',
      vip_op: 'add',
      loyalty: '',
      loyalty_op: 'add',
    });
  };

  const handleCloseBalanceDialog = () => {
    setBalanceDialog({ open: false, user: null });
    setBalanceForm({
      credit: '',
      credit_op: 'add',
      bonus: '',
      bonus_op: 'add',
      vip: '',
      vip_op: 'add',
      loyalty: '',
      loyalty_op: 'add',
    });
  };

  const handleSaveBalance = async () => {
    if (!balanceDialog.user) return;
    try {
      const token = localStorage.getItem('token');
      const payload: any = {};
      if (balanceForm.credit && !isNaN(Number(balanceForm.credit))) {
        payload.credit = balanceForm.credit;
        payload.credit_op = balanceForm.credit_op;
      }
      if (balanceForm.bonus && !isNaN(Number(balanceForm.bonus))) {
        payload.bonus = balanceForm.bonus;
        payload.bonus_op = balanceForm.bonus_op;
      }
      if (balanceForm.vip && !isNaN(Number(balanceForm.vip))) {
        payload.vip = balanceForm.vip;
        payload.vip_op = balanceForm.vip_op;
      }
      if (balanceForm.loyalty && !isNaN(Number(balanceForm.loyalty))) {
        payload.loyalty = balanceForm.loyalty;
        payload.loyalty_op = balanceForm.loyalty_op;
      }
      await api.post(`/api/v1/admin/user/${balanceDialog.user.id}/update-balances`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setSnackbar({ open: true, message: 'Balances updated', severity: 'success' });
      fetchUsers();
      handleCloseBalanceDialog();
    } catch {
      setSnackbar({ open: true, message: 'Failed to update balances', severity: 'error' });
    }
  };

  const handleOpenPlatformCredentialsDialog = async (user: User) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/v1/admin/user/${user.id}/platform-credentials`, { headers: { Authorization: `Bearer ${token}` } });
      setPlatformCredentialsDialog({ open: true, user, credentials: res.data as PlatformCredential[] });
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch platform credentials', severity: 'error' });
    }
  };

  const handleClosePlatformCredentialsDialog = () => {
    setPlatformCredentialsDialog({ open: false, user: null, credentials: [] });
    setEditingCredential({ credential: null, username: '', password: '' });
  };

  const handleEditCredential = (credential: PlatformCredential) => {
    setEditingCredential({
      credential,
      username: credential.platform_username || '',
      password: credential.platform_password || ''
    });
  };

  const handleSaveCredential = async () => {
    if (!editingCredential.credential || !platformCredentialsDialog.user) return;
    try {
      const token = localStorage.getItem('token');
      const payload: any = {};
      if (editingCredential.username !== editingCredential.credential.platform_username) {
        payload.platform_username = editingCredential.username;
      }
      if (editingCredential.password !== editingCredential.credential.platform_password) {
        payload.platform_password = editingCredential.password;
      }
      
      if (Object.keys(payload).length > 0) {
        await api.put(
          `/api/v1/admin/user/${platformCredentialsDialog.user.id}/platform-credentials/${editingCredential.credential.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSnackbar({ open: true, message: 'Platform credential updated', severity: 'success' });
        
        // Refresh the credentials
        const res = await api.get(`/api/v1/admin/user/${platformCredentialsDialog.user.id}/platform-credentials`, { headers: { Authorization: `Bearer ${token}` } });
        setPlatformCredentialsDialog(prev => ({ ...prev, credentials: res.data as PlatformCredential[] }));
      }
      
      setEditingCredential({ credential: null, username: '', password: '' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to update platform credential', severity: 'error' });
    }
  };

  const handleCancelEditCredential = () => {
    setEditingCredential({ credential: null, username: '', password: '' });
  };

  const handleOpenHistoryDialog = async (user: User) => {
    setHistoryDialog({ open: true, user, transactions: [], loading: true });
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/v1/admin/user/${user.id}/transaction-history`, { 
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 }
      });
      setHistoryDialog(prev => ({ ...prev, transactions: res.data as Transaction[], loading: false }));
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch transaction history', severity: 'error' });
      setHistoryDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialog({ open: false, user: null, transactions: [], loading: false });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Users</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          label="Search by user ID, email, name, or phone"
          value={search}
          onChange={handleSearchChange}
          sx={{ mb: 2, width: 350 }}
        />
        {tenantId && (
          <>
            <Button variant="contained" onClick={() => handleOpenDialog()} sx={{ ml: 2 }}>Create User</Button>
            <Button variant="outlined" onClick={() => setImportOpen(true)} sx={{ ml: 2 }}>Import CSV</Button>
          </>
        )}
      </Paper>
      <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1400 }}>
                     <TableHead>
             <TableRow>
               <TableCell sx={{ fontWeight: 'bold' }}>User ID</TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>
                 <TableSortLabel
                   active={sortBy === 'email'}
                   direction={sortBy === 'email' ? sortOrder : 'asc'}
                   onClick={createSortHandler('email')}
                 >
                   Email
                 </TableSortLabel>
               </TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>
                 <TableSortLabel
                   active={sortBy === 'first_name'}
                   direction={sortBy === 'first_name' ? sortOrder : 'asc'}
                   onClick={createSortHandler('first_name')}
                 >
                   Name
                 </TableSortLabel>
               </TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>Referral</TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>
                 <TableSortLabel
                   active={sortBy === 'affiliate_id'}
                   direction={sortBy === 'affiliate_id' ? sortOrder : 'asc'}
                   onClick={createSortHandler('affiliate_id')}
                 >
                   Affiliate
                 </TableSortLabel>
               </TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>
                 <TableSortLabel
                   active={sortBy === 'individual_ggr'}
                   direction={sortBy === 'individual_ggr' ? sortOrder : 'asc'}
                   onClick={createSortHandler('individual_ggr')}
                 >
                   Individual GGR
                 </TableSortLabel>
               </TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>
                 <TableSortLabel
                   active={sortBy === 'credit_balance'}
                   direction={sortBy === 'credit_balance' ? sortOrder : 'asc'}
                   onClick={createSortHandler('credit_balance')}
                 >
                   Credit Balance
                 </TableSortLabel>
               </TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>Other Balances</TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>Registration IP</TableCell>
               <TableCell sx={{ fontWeight: 'bold' }}>Last Login IP</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fraud Score</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Device ID</TableCell>
             </TableRow>
           </TableHead>
                     <TableBody>
             {users.map(user => (
               <React.Fragment key={user.id}>
                 {/* Main user data row */}
                 <TableRow>
                   <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{user.id}</TableCell>
                   <TableCell>{user.email}</TableCell>
                   <TableCell>{user.first_name} {user.last_name}</TableCell>
                   <TableCell>{user.phone_number}</TableCell>
                   <TableCell>{user.referral_id}</TableCell>
                   <TableCell>{user.affiliate_id}</TableCell>
                   <TableCell>
                     <Typography 
                       variant="body2" 
                       fontWeight="bold" 
                       color={user.individual_ggr >= 0 ? 'success.main' : 'error.main'}
                     >
                       {new Intl.NumberFormat('en-US', {
                         style: 'currency',
                         currency: 'USD',
                         minimumFractionDigits: 2,
                         maximumFractionDigits: 2,
                       }).format(user.individual_ggr)}
                     </Typography>
                   </TableCell>
                   <TableCell>
                     <Typography variant="body2" fontWeight="bold">
                       {new Intl.NumberFormat('en-US', {
                         style: 'currency',
                         currency: 'USD',
                         minimumFractionDigits: 2,
                         maximumFractionDigits: 2,
                       }).format(parseFloat(user.credit_balance) || 0)}
                     </Typography>
                   </TableCell>
                   <TableCell>
                     <Stack direction="column" spacing={0.5}>
                       <span>Bonus: {user.bonus_balance}</span>
                       <span>VIP: {user.vip_balance}</span>
                       <span>Loyalty: {user.loyalty_balance}</span>
                     </Stack>
                   </TableCell>
                   <TableCell>
                     <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                       {user.registration_ip || 'N/A'}
                     </Typography>
                   </TableCell>
                   <TableCell>
                     <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                       {user.last_login_ip || 'N/A'}
                     </Typography>
                   </TableCell>
                   <TableCell>
                     <Typography
                       variant="body2"
                       fontWeight="bold"
                       sx={{
                         color: user.fraud_score === null || user.fraud_score === undefined
                           ? 'text.secondary'
                           : user.fraud_score >= 85
                             ? 'error.main'
                             : user.fraud_score >= 50
                               ? 'warning.main'
                               : 'success.main'
                       }}
                     >
                       {user.fraud_score !== null && user.fraud_score !== undefined ? user.fraud_score : 'N/A'}
                     </Typography>
                   </TableCell>
                   <TableCell>
                     <Typography
                       variant="body2"
                       sx={{
                         fontFamily: 'monospace',
                         fontSize: '0.75rem',
                         maxWidth: '120px',
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}
                       title={user.device_id || 'N/A'}
                     >
                       {user.device_id || 'N/A'}
                     </Typography>
                   </TableCell>
                 </TableRow>
                 {/* Actions row */}
                 <TableRow sx={{ backgroundColor: 'grey.50' }}>
                   <TableCell colSpan={12} sx={{ py: 1 }}>
                     <Stack direction="row" spacing={1} alignItems="center">
                       <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                         Actions:
                       </Typography>
                       <IconButton size="small" onClick={() => handleOpenDialog(user)} title="Edit">
                         <EditIcon fontSize="small" />
                       </IconButton>
                       <IconButton size="small" onClick={() => handleOpenBalanceDialog(user)} title="Edit Balances">
                         <MonetizationOnIcon fontSize="small" />
                       </IconButton>
                       <IconButton size="small" onClick={() => handleOpenPlatformCredentialsDialog(user)} title="Platform Credentials">
                         <AccountCircleIcon fontSize="small" />
                       </IconButton>
                       <IconButton size="small" onClick={() => handleOpenHistoryDialog(user)} title="Transaction History">
                         <HistoryIcon fontSize="small" />
                       </IconButton>
                       <IconButton size="small" color="error" onClick={() => handleDelete(user)} title="Delete">
                         <DeleteIcon fontSize="small" />
                       </IconButton>
                     </Stack>
                   </TableCell>
                 </TableRow>
               </React.Fragment>
             ))}
           </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            Page size:
          </Typography>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1); // Reset to first page when changing page size
            }}
            size="small"
            sx={{ minWidth: 80 }}
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
          </Select>
          <Typography variant="body2">
            Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, pagination.total_users)} of {pagination.total_users} users
          </Typography>
        </Box>
        <Pagination
          count={pagination.total_pages}
          page={page}
          onChange={handlePageChange}
          showFirstButton
          showLastButton
          color="primary"
        />
      </Box>
      
      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          <TextField label="Email" fullWidth margin="normal" value={form.email} onChange={e => setForm((f: any) => ({ ...f, email: e.target.value }))} />
          <TextField label="Password" type="password" fullWidth margin="normal" value={form.password} onChange={e => setForm((f: any) => ({ ...f, password: e.target.value }))} helperText={editUser ? 'Leave blank to keep current password' : ''} />
          <TextField label="First Name" fullWidth margin="normal" value={form.first_name} onChange={e => setForm((f: any) => ({ ...f, first_name: e.target.value }))} />
          <TextField label="Last Name" fullWidth margin="normal" value={form.last_name} onChange={e => setForm((f: any) => ({ ...f, last_name: e.target.value }))} />
          <TextField label="Phone Number" fullWidth margin="normal" value={form.phone_number} onChange={e => setForm((f: any) => ({ ...f, phone_number: e.target.value }))} />
          <TextField label="Referral ID" fullWidth margin="normal" value={form.referral_id} onChange={e => setForm((f: any) => ({ ...f, referral_id: e.target.value }))} />
          <TextField label="Affiliate ID" fullWidth margin="normal" value={form.affiliate_id} onChange={e => setForm((f: any) => ({ ...f, affiliate_id: e.target.value }))} />
          {editUser && (
            <>
              <TextField 
                label="Registration IP" 
                fullWidth 
                margin="normal" 
                value={form.registration_ip || 'N/A'} 
                InputProps={{ readOnly: true }}
                helperText="IP address used during registration"
              />
              <TextField 
                label="Last Login IP" 
                fullWidth 
                margin="normal" 
                value={form.last_login_ip || 'N/A'} 
                InputProps={{ readOnly: true }}
                helperText="IP address used during last login"
              />
              {form.last_login && (
                <TextField 
                  label="Last Login Time" 
                  fullWidth 
                  margin="normal" 
                  value={formatDate(form.last_login)} 
                  InputProps={{ readOnly: true }}
                  helperText="When the user last logged in"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Balances Dialog */}
      <Dialog open={balanceDialog.open} onClose={handleCloseBalanceDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Balances</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Credit Operation</InputLabel>
            <Select
              value={balanceForm.credit_op}
              label="Credit Operation"
              onChange={e => setBalanceForm(f => ({ ...f, credit_op: e.target.value }))}
            >
              <MenuItem value="add">Add</MenuItem>
              <MenuItem value="remove">Remove</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Credit Amount" fullWidth margin="normal" value={balanceForm.credit} onChange={e => setBalanceForm(f => ({ ...f, credit: e.target.value }))} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Bonus Operation</InputLabel>
            <Select
              value={balanceForm.bonus_op}
              label="Bonus Operation"
              onChange={e => setBalanceForm(f => ({ ...f, bonus_op: e.target.value }))}
            >
              <MenuItem value="add">Add</MenuItem>
              <MenuItem value="remove">Remove</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Bonus Amount" fullWidth margin="normal" value={balanceForm.bonus} onChange={e => setBalanceForm(f => ({ ...f, bonus: e.target.value }))} />
          <FormControl fullWidth margin="normal">
            <InputLabel>VIP Operation</InputLabel>
            <Select
              value={balanceForm.vip_op}
              label="VIP Operation"
              onChange={e => setBalanceForm(f => ({ ...f, vip_op: e.target.value }))}
            >
              <MenuItem value="add">Add</MenuItem>
              <MenuItem value="remove">Remove</MenuItem>
            </Select>
          </FormControl>
          <TextField label="VIP Amount" fullWidth margin="normal" value={balanceForm.vip} onChange={e => setBalanceForm(f => ({ ...f, vip: e.target.value }))} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Loyalty Operation</InputLabel>
            <Select
              value={balanceForm.loyalty_op}
              label="Loyalty Operation"
              onChange={e => setBalanceForm(f => ({ ...f, loyalty_op: e.target.value }))}
            >
              <MenuItem value="add">Add</MenuItem>
              <MenuItem value="remove">Remove</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Loyalty Amount" fullWidth margin="normal" value={balanceForm.loyalty} onChange={e => setBalanceForm(f => ({ ...f, loyalty: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBalanceDialog}>Cancel</Button>
          <Button onClick={handleSaveBalance} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Platform Credentials Dialog */}
      <Dialog open={platformCredentialsDialog.open} onClose={handleClosePlatformCredentialsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Platform Credentials - {platformCredentialsDialog.user?.email}
        </DialogTitle>
        <DialogContent>
          {platformCredentialsDialog.credentials.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No platform credentials found for this user.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Platform</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Password</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {platformCredentialsDialog.credentials.map((credential) => (
                    <TableRow key={credential.id}>
                      <TableCell>
                        <Chip 
                          label={credential.platform_name} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {editingCredential.credential?.id === credential.id ? (
                          <TextField
                            size="small"
                            value={editingCredential.username}
                            onChange={(e) => setEditingCredential(prev => ({ ...prev, username: e.target.value }))}
                            fullWidth
                          />
                        ) : (
                          credential.platform_username || 'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {editingCredential.credential?.id === credential.id ? (
                          <TextField
                            size="small"
                            type="password"
                            value={editingCredential.password}
                            onChange={(e) => setEditingCredential(prev => ({ ...prev, password: e.target.value }))}
                            fullWidth
                          />
                        ) : (
                          credential.platform_password ? '••••••••' : 'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={credential.is_active ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={credential.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {editingCredential.credential?.id === credential.id ? (
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="contained" onClick={handleSaveCredential}>
                              Save
                            </Button>
                            <Button size="small" onClick={handleCancelEditCredential}>
                              Cancel
                            </Button>
                          </Stack>
                        ) : (
                          <IconButton size="small" onClick={() => handleEditCredential(credential)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlatformCredentialsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={historyDialog.open} onClose={handleCloseHistoryDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Transaction History - {historyDialog.user?.email}
        </DialogTitle>
        <DialogContent>
          {historyDialog.loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : historyDialog.transactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No transaction history found for this user.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Payment Method</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyDialog.transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {formatDate(transaction.initiated_at)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.transaction_type} 
                          size="small" 
                          color={
                            transaction.transaction_type === 'TOP_UP' || transaction.transaction_type === 'DEPOSIT' 
                              ? 'success' 
                              : transaction.transaction_type === 'WITHDRAWAL' 
                                ? 'error' 
                                : 'default'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold" 
                          color={
                            transaction.transaction_type === 'TOP_UP' || transaction.transaction_type === 'DEPOSIT' 
                              ? 'success.main' 
                              : transaction.transaction_type === 'WITHDRAWAL' 
                                ? 'error.main' 
                                : 'text.primary'
                          }
                        >
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.payment_method || 'N/A'} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {transaction.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>
                          {transaction.payment_reference || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      
      <UserImportDialog
        open={importOpen}
        tenantId={tenantId}
        onClose={() => setImportOpen(false)}
        onImported={fetchUsers}
      />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantUsersPage; 